import { BehaviorSubject, Observable, catchError, finalize, map, of, tap, throwError } from 'rxjs'
import { Booking } from '@app/domain/Booking'
import { BookingDTO } from '@services/DTO/BookingDTO'
import { BookingStatus, BookingStatusType } from '@app/domain/BookingStatus'
import { BookingsCacheService } from '@services/bookings-cache.service'
import { Business } from '@app/domain/Business'
import { CancellationReason } from '@app/domain/CancellationReason'
import { Charge } from '../domain/Charge'
import { Contact } from '../domain/Contact'
import { Customer } from '../domain/Customer'
import { DTOAdapter } from '@services/DTOAdapter'
import { DateTime } from 'luxon'
import { Helper } from '@app/helpers/utilities/helper'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ModelCloningService } from '@services/model-cloning.service'
import { Organisation } from '@app/domain/Organisation'
import { Page } from '@app/domain/Page'
import { Table } from '@app/domain/Table'
import { AccountUser } from '@app/domain/AccountUser'
import { Venue } from '@app/domain/Venue'
import { compare } from 'fast-json-patch'
import { environment } from '@environments/environment'
import { v4 as uuidv4 } from 'uuid'
import { Order } from '@app/domain/Order'
import { ServiceStatus } from '@app/domain/ServiceStatus'
import { BookingDocket } from '@app/domain/BookingDocket'
import { VenueCalendarDTO } from '@services/DTO/VenueCalendarDTO'
import { VenueSchedule } from '@app/domain/VenueSchedule'

export type VenueCalendar = { bookings: Booking[], schedule: VenueSchedule }

export type AddBookingOptions = {
    sendViaEmail?: boolean,
    sendViaSMS?: boolean,
    userPinCode?: string,
}

@Injectable({
    providedIn: 'root',
})
export class BookingService {

    private _loading$ = new BehaviorSubject<boolean>(true)
    calendarDataInfo$ = new BehaviorSubject<{ dataAt: Date, isFromCache: boolean } | null>(null)

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
        private modelCloningService: ModelCloningService,
        private bookingsCacheService: BookingsCacheService
    ) { }

    getBooking(bookingId: string): Observable<Booking> {
        const path =
            `/organisation/booking/${bookingId}`
        const url = new URL(path, environment.apiBaseURL)
        this._loading$.next(true)
        return this.http.get<BookingDTO>(url.toString())
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    removeCancellation(organisationId: string, businessId: string, venueId: string, bookingId: string, cancellationId: string): Observable<Booking> {
        const path = `/organisation/${organisationId}/business/${businessId}/venue/${venueId}/booking/${bookingId}/cancellation/${cancellationId}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<BookingDTO>(url.toString())
            .pipe(map(dto => this.dtoAdapter.adaptBookingDto(dto)))
    }

    getBookingsByIds(bookingIds: string[]): Observable<Booking[]> {
        if (bookingIds.length === 0) {
            return of([])
        }
        const path = `/organisation/booking/batch`
        const url = new URL(path, environment.apiBaseURL)
        const params = new HttpParams().set('ids', bookingIds.join(','))
        return this.http.get<BookingDTO[]>(url.toString(), { params })
            .pipe(
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptBookingDto(dto)))
            )
    }

    getBookings(
        businessId: string,
        venueId: string,
        startDateTime: Date,
        endDateTime: Date,
        statuses?: BookingStatusType[]
    ): Observable<Booking[]> {
        const localStartDateTime = DateTime.fromJSDate(startDateTime)
            .setZone(environment.assumedVenueTimeZone)
            .toISO()!
        const localEndDateTime = DateTime.fromJSDate(endDateTime)
            .setZone(environment.assumedVenueTimeZone)
            .toISO()!
        const cacheDate = DateTime.fromJSDate(startDateTime)
            .setZone(environment.assumedVenueTimeZone)
            .toISODate()!
        let params = new HttpParams()
            .set('startTime', localStartDateTime)
            .set('endTime', localEndDateTime)
        if (statuses) {
            params = params.set('statuses', statuses.join(','))
        }
        const path = `/organisation
/business/${businessId}
/venue/${venueId}
/booking`
        const url = new URL(path, environment.apiBaseURL)
        this._loading$.next(true)
        return this.http.get<BookingDTO[]>(url.toString(), { params })
            .pipe(
                tap(dtos => {
                    this.bookingsCacheService.store(venueId, cacheDate, dtos)
                }),
                catchError(error => {
                    const cached = this.bookingsCacheService.retrieve(venueId, cacheDate)
                    if (cached) {
                        return of(cached.dtos as BookingDTO[])
                    }
                    return throwError(() => error)
                }),
                finalize(() => this._loading$.next(false)),
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptBookingDto(dto)))
            )
    }

    getCalendar(
        organisationId: string,
        businessId: string,
        venue: Venue,
        startDate: Date,
        endDate: Date,
        statuses?: BookingStatusType[]
    ): Observable<VenueCalendar> {
        const start = DateTime.fromJSDate(startDate).setZone(venue.timeZone).toISODate()!
        const end = DateTime.fromJSDate(endDate).setZone(venue.timeZone).toISODate()!
        let params = new HttpParams().set('startDate', start).set('endDate', end)
        if (statuses) {
            params = params.set('statuses', statuses.join(','))
        }
        const url = new URL(`/organisation/${organisationId}/business/${businessId}/venue/${venue.id}/calendar`, environment.apiBaseURL)
        const adaptCalendar = (dto: VenueCalendarDTO): VenueCalendar => ({
            bookings: dto.bookings.map(booking => this.dtoAdapter.adaptBookingDto(booking)),
            schedule: this.dtoAdapter.adaptVenueScheduleDto(dto.schedule),
        })
        this._loading$.next(true)
        return this.http.get<VenueCalendarDTO>(url.toString(), { params }).pipe(
            map(dto => {
                const calendar = adaptCalendar(dto)
                this.bookingsCacheService.storeCalendar(venue.id, start, end, dto, statuses)
                this.calendarDataInfo$.next({ dataAt: new Date(), isFromCache: false })
                return calendar
            }),
            catchError(error => {
                try {
                    const cached = this.bookingsCacheService.retrieveCalendar(venue.id, start, end, statuses)
                    if (cached) {
                        const calendar = adaptCalendar(cached.dto)
                        this.calendarDataInfo$.next({ dataAt: cached.cachedAt, isFromCache: true })
                        return of(calendar)
                    }
                } catch {
                    // An unusable cache must not mask the original load error.
                }
                return throwError(() => error)
            }),
            finalize(() => this._loading$.next(false)),
        )
    }

    getBookingsByDateBooked(
        organisationId: string,
        businessId: string,
        venueId: string,
        startDateTime: Date,
        endDateTime: Date,
        statuses?: BookingStatusType[]
    ): Observable<Booking[]> {
        const localStartDateTime = DateTime.fromJSDate(startDateTime)
            .setZone(environment.assumedVenueTimeZone)
            .toISO()!
        const localEndDateTime = DateTime.fromJSDate(endDateTime)
            .setZone(environment.assumedVenueTimeZone)
            .toISO()!
        let params = new HttpParams()
            .set('startTime', localStartDateTime)
            .set('endTime', localEndDateTime)
        if (statuses) {
            params = params.set('statuses', statuses.join(','))
        }
        const path = `/organisation/${organisationId}` +
        `/business/${businessId}` +
        `/venue/${venueId}` +
        '/booking?byDateBooked=true'
        const url = new URL(path, environment.apiBaseURL)
        this._loading$.next(true)
        return this.http.get<BookingDTO[]>(url.toString(), { params })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptBookingDto(dto)))
            )
    }

    searchBookings(
        businessId: string,
        venueId: string,
        isInFuture: boolean,
        searchTerm: string,
        page: number,
        pageSize: number,
        direction: 'asc' | 'desc'
    ): Observable<Page<Booking>> {
        const path = `/organisation
/business/${businessId}
/venue/${venueId}
/booking
/search`
        const url = new URL(path, environment.apiBaseURL)
        const params = {
            isInFuture: isInFuture.toString(),
            searchTerm: searchTerm,
            page: page.toString(),
            pageSize: pageSize.toString(),
            direction: direction,
        }
        this._loading$.next(true)
        return this.http.get<Page<BookingDTO>>(url.toString(), { params })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(page => {
                    return {
                        ...page,
                        items: page.items.map(dto => this.dtoAdapter.adaptBookingDto(dto)),
                    }
                })
            )
    }

    getBookingsWithOrders(
        organisationId: string,
        businessId: string,
        venue: Venue,
        incompleteOnly: boolean,
        includeCancelled: boolean,
        isInFuture: boolean,
        page: number,
        pageSize: number,
    ): Observable<Page<Booking>> {
        const path = `/organisation/${organisationId}/business/${businessId}/venue/${venue.id}/booking/orders`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<Page<BookingDTO>>(url.toString(), {
            params: { incompleteOnly, includeCancelled, isInFuture, page, pageSize },
        }).pipe(
            map(result => ({
                ...result,
                items: result.items.map(dto => this.dtoAdapter.adaptBookingDto(dto, venue.timeZone)),
            })),
        )
    }

    getCustomerBookings(
        organisationId: string,
        businessId: string,
        customer: Customer,
        page: number,
        pageSize: number,
        direction: 'asc' | 'desc'
    ): Observable<Page<Booking>> {
        const path = `/organisation/${organisationId}
/business/${businessId}
/venue/${customer.venueId}
/booking`
        const url = new URL(path, environment.apiBaseURL)
        const params = {
            customerId: customer.id,
            page: page.toString(),
            pageSize: pageSize.toString(),
            direction: direction,
        }
        this._loading$.next(true)
        return this.http.get<Page<BookingDTO>>(url.toString(), { params })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(page => {
                    return {
                        ...page,
                        items: page.items.map(dto => this.dtoAdapter.adaptBookingDto(dto)),
                    }
                })
            )
    }

    addBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        booking: Booking,
        idempotencyKey: string,
        options?: Partial<AddBookingOptions>
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            '/booking'
        const dto: any = this.dtoAdapter.adaptBooking(booking)
        const url = new URL(path, environment.apiBaseURL)
        if (options?.sendViaEmail) {
            url.searchParams.append('sendViaEmail', 'true')
        }
        if (options?.sendViaSMS) {
            url.searchParams.append('sendViaSMS', 'true')
        }
        if (options?.userPinCode) {
            dto['userPinCode'] = options.userPinCode
        }
        const headers = {
            'Idempotency-Key': idempotencyKey,
        }
        this._loading$.next(true)
        return this.http.post<BookingDTO>(url.toString(), dto, { headers })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    addBookings(
        businessId: string,
        venueId: string,
        bookings: Booking[],
        idempotencyKey: string
    ): Observable<Booking[]> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/booking' +
            '/multiple'
        const dtos = bookings.map(booking => this.dtoAdapter.adaptBooking(booking))
        const url = new URL(path, environment.apiBaseURL)
        const headers = {
            'Idempotency-Key': idempotencyKey,
        }
        this._loading$.next(true)
        return this.http.post<BookingDTO[]>(url.toString(), dtos, { headers })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptBookingDto(dto)))
            )
    }

    acceptBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Booked
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    cancelBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        user: AccountUser,
        paymentOptions: Partial<{
            mechanism: 'cancellationCharge' | 'depositRefund',
            coversToCharge: number | null,
            orderRefundLineIds: string[],
        }>,
        cancellationReason: CancellationReason | null = null
    ): Observable<Booking> {
        const metadata = new Map<string, any | null>()
        metadata.set('cancelledByCustomer', false)
        metadata.set('cancelledByUserId', user.id)
        metadata.set('cancelledByUserEmailAddress', user.emailAddress)
        if (cancellationReason) {
            metadata.set('cancellationReasonId', cancellationReason.id)
            metadata.set('cancellationReasonDisplayName', cancellationReason.displayName)
        }
        const status = new BookingStatus(
            uuidv4(),
            metadata,
            new Date(),
            BookingStatusType.Cancelled
        )
        if (paymentOptions.orderRefundLineIds?.length && paymentOptions.mechanism !== 'cancellationCharge') {
            return this.refundOrderLines(businessId, venueId, areaId, bookingId, paymentOptions.orderRefundLineIds,
                status, paymentOptions.mechanism === 'depositRefund')
        }
        if (paymentOptions.mechanism === 'cancellationCharge') {
            return this.chargeCancellationFee(
                businessId,
                venueId,
                areaId,
                bookingId,
                status,
                paymentOptions.coversToCharge ?? null,
                paymentOptions.orderRefundLineIds ?? []
            )
        } else if (paymentOptions.mechanism === 'depositRefund') {
            return this.refundDeposit(
                businessId,
                venueId,
                areaId,
                bookingId,
                status
            )
        }
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    noShowBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        paymentOptions: Partial<{
            mechanism: 'cancellationCharge' | 'depositRefund',
            coversToCharge: number | null,
            orderRefundLineIds: string[],
        }>
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.NoShow
        )
        if (paymentOptions.orderRefundLineIds?.length && paymentOptions.mechanism !== 'cancellationCharge') {
            return this.refundOrderLines(businessId, venueId, areaId, bookingId, paymentOptions.orderRefundLineIds,
                status, paymentOptions.mechanism === 'depositRefund')
        }
        if (paymentOptions.mechanism === 'cancellationCharge') {
            return this.chargeCancellationFee(
                businessId,
                venueId,
                areaId,
                bookingId,
                status,
                paymentOptions.coversToCharge ?? null,
                paymentOptions.orderRefundLineIds ?? []
            )
        } else if (paymentOptions.mechanism === 'depositRefund') {
            return this.refundDeposit(
                businessId,
                venueId,
                areaId,
                bookingId,
                status
            )
        }
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    setPartyToWaiting(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Waiting
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    partiallySeatBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.PartiallySeated
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    seatBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        booking: Booking
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Seated
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            booking.id,
            status
        )
    }

    unseatBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Booked
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    markDepositAsManuallyPaid(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Booked
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status,
            true
        )
    }

    finishBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Finished
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    finishBookingEarly(
        businessId: string,
        venueId: string,
        bookingId: string,
        finishFromNowMinutes?: number
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/finish'
        const url = new URL(path, environment.apiBaseURL)
        const queryParameters = {
            finishFromNowMinutes: finishFromNowMinutes?.toString() || '0',
        }
        return this.http.post<BookingDTO>(url.toString(), null, { params: queryParameters })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    finishAllInProgressBookings(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        date: Date
    ) {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            '/booking/finish'
        const url = new URL(path, environment.apiBaseURL)
        const queryParameters = {
            date: Helper.makeLocalISOFormattedDateString(date),
        }
        return this.http.post<BookingDTO[]>(url.toString(), null, { params: queryParameters })
    }

    rejectBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        reason: string | null
    ): Observable<Booking> {
        const metadata = new Map<string, any | null>()
        if (reason) {
            metadata.set('reason', reason)
        }
        const status = new BookingStatus(
            uuidv4(),
            metadata,
            new Date(),
            BookingStatusType.Rejected
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    reseatBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const status = new BookingStatus(
            uuidv4(),
            new Map(),
            new Date(),
            BookingStatusType.Seated
        )
        return this.addStatusToBooking(
            businessId,
            venueId,
            areaId,
            bookingId,
            status
        )
    }

    markBookingAsRunningLate(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string
    ): Observable<Booking> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/service/running-late'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<BookingDTO>(url.toString(), null)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    updateServiceStatus(
        businessId: string,
        venueId: string,
        bookingId: string,
        serviceStatus: ServiceStatus | null
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/service-status'
        const url = new URL(path, environment.apiBaseURL)
        const body = {
            serviceStatus: serviceStatus === null ? null : this.dtoAdapter.adaptServiceStatus(serviceStatus),
        }
        return this.http.put<BookingDTO>(url.toString(), body)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    voidBooking(
        businessId: string,
        venueId: string,
        booking: Booking
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${booking.id}` +
            '/void'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<BookingDTO>(url.toString(), null)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    patchBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        booking: Booking,
        updatedBooking: Booking
    ): Observable<Booking> {
        const bookingDTO = this.dtoAdapter.adaptBooking(booking)
        const updatedBookingDTO = this.dtoAdapter.adaptBooking(updatedBooking)
        const patch = compare(bookingDTO, updatedBookingDTO)
        let body = Helper.rewriteUndefinedValuesAsNull(patch)
        body = Helper.rewriteEmptyValuesAsNull(body)
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            `/booking/${bookingId}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.patch<BookingDTO>(url.toString(),body)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    moveBookingsToTables(
        businessId: string,
        venueId: string,
        bookingsOnTablesAtNewDates: {
            booking: Booking,
            tables: Table[],
            start?: Date,
        }[]
    ): Observable<Booking[]> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/booking/move'
        const url = new URL(path, environment.apiBaseURL)
        const dtos = bookingsOnTablesAtNewDates.map(({ booking, tables, start }) => {
            return this.dtoAdapter.adaptMoveBookingRequest(booking, tables, start)
        })
        return this.http.post<BookingDTO[]>(url.toString(), dtos)
            .pipe(
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptBookingDto(dto)))
            )
    }

    changeBookingDate(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string,
        newStartDateTime: Date,
        newDuration?: number,
        newTableIds?: string[],
        newStatus?: BookingStatus
    ): Observable<Booking> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/start'
        const url = new URL(path, environment.apiBaseURL)
        const localStartDateTime = DateTime.fromJSDate(newStartDateTime)
            .setZone(environment.assumedVenueTimeZone)
            .toISO()!
        let params = new HttpParams()
            .set('start', localStartDateTime)
        if (newDuration) {
            params = params.set('duration', newDuration.toString())
        }
        if (newTableIds) {
            params = params.set('tableIds', newTableIds.join(','))
        }
        const body = newStatus ? this.dtoAdapter.adaptBookingStatus(newStatus) : null
        return this.http.post<BookingDTO>(url.toString(), body, { params })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    createNewCustomerAttachedToBooking(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string,
        contact: Contact
    ): Observable<Booking> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/customer'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<BookingDTO>(url.toString(), contact)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    attachCustomerToBooking(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string,
        customerId: string
    ): Observable<Booking> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/customer'
        const queryParameters = {
            customerId: customerId,
        }
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<BookingDTO>(url.toString(), null, { params: queryParameters })
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    detachCustomerFromBooking(
        organisationId: string,
        businessId: string,
        venueId: string,
        bookingId: string
    ): Observable<Booking> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/customer'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<BookingDTO>(url.toString())
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    getVenueActiveTableCount(
        businessId: string,
        venueId: string,
        startDateTime: Date,
        endDateTime: Date
    ): Observable<number> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/booking/count'
        const url = new URL(path, environment.apiBaseURL)
        const params = new HttpParams()
            .set('startTime', Helper.makeLocalISOFormattedDateTimeString(startDateTime))
            .set('endTime', Helper.makeLocalISOFormattedDateTimeString(endDateTime))
        return this.http.get<number>(url.toString(), { params })
    }


    bookingConfirmationResendRequested(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        booking: Booking,
        sendViaEmail: boolean = true,
        sendViaSMS: boolean = false
    ) {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/booking/${booking.id}` +
            '/booking-confirmation-resend' +
            '?sendViaEmail=' + sendViaEmail +
            '&sendViaSMS=' + sendViaSMS
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post(url.toString(), null)
    }

    addCancellationCharge(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        booking: Booking,
        charge: Charge,
        sendViaEmail: boolean,
        sendViaSMS: boolean,
        expiryDate: Date | null
    ): Observable<Booking> {
        let path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/booking/${booking.id}` +
            '/cancellation' +
            '?sendViaEmail=' + sendViaEmail +
            '&sendViaSMS=' + sendViaSMS
        const expiryLocalDateTime = expiryDate ? DateTime.fromJSDate(expiryDate)
            .setZone(venue.timeZone)
            .toISO() : null
        if (expiryLocalDateTime !== null) {
            path += '&expiryDate=' + encodeURIComponent(expiryLocalDateTime)
        }
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<BookingDTO>(url.toString(), charge)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    addDepositCharge(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        booking: Booking,
        charge: Charge,
        sendViaEmail: boolean,
        sendViaSMS: boolean,
        expiryDate: Date | null,
        isPaidImmediately: boolean
    ): Observable<Booking> {
        let path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/booking/${booking.id}` +
            '/deposit' +
            '?sendViaEmail=' + sendViaEmail +
            '&sendViaSMS=' + sendViaSMS +
            '&isImmediatelyPaid=' + isPaidImmediately
        const expiryLocalDateTime = expiryDate ? DateTime.fromJSDate(expiryDate)
            .setZone(venue.timeZone)
            .toISO() : null
        if (expiryLocalDateTime !== null) {
            path += '&expiryDate=' + encodeURIComponent(expiryLocalDateTime)
        }
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<BookingDTO>(url.toString(), charge)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    updateCharge(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        booking: Booking,
        charge: Charge,
        updatedExpiryDate?: Date | null
    ): Observable<Booking> {
        let path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/booking/${booking.id}` +
            '/charge'
        const expiryLocalDateTime = updatedExpiryDate ? DateTime.fromJSDate(updatedExpiryDate)
            .setZone(venue.timeZone)
            .toISO() : null
        if (updatedExpiryDate !== undefined) {
            if (updatedExpiryDate === null) {
                path += '?removeExpiryDate=true'
            } else {
                path += `?expiryDate=${encodeURIComponent(expiryLocalDateTime!)}`
            }
        }
        const url = new URL(path, environment.apiBaseURL)
        return this.http.put<BookingDTO>(url.toString(), charge)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    bookingPendingPaymentResendRequested(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        booking: Booking,
        sendViaEmail: boolean,
        sendViaSMS: boolean
    ) {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/booking/${booking.id}` +
            '/pending-payment-resend' +
            '?sendViaEmail=' + sendViaEmail +
            '&sendViaSMS=' + sendViaSMS
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post(url.toString(), null)
    }

    resendPreOrderMenuLink(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        booking: Booking,
        sendViaEmail: boolean,
        sendViaSMS: boolean
    ) {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/booking/${booking.id}` +
            '/pre-order-menu-resend' +
            '?sendViaEmail=' + sendViaEmail +
            '&sendViaSMS=' + sendViaSMS
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post(url.toString(), null)
    }

    private addStatusToBooking(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        status: BookingStatus,
        markDepositAsPaid = false
    ): Observable<Booking> {
        const dto = this.dtoAdapter.adaptBookingStatus(status)
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            `/booking/${bookingId}` +
            '/status'
        let params = new HttpParams()
        if (markDepositAsPaid) {
            params = params.set('markPendingDepositAsPaid', 'true')
        }
        const url = new URL(path, environment.apiBaseURL)
        this._loading$.next(true)
        return this.http.put<BookingDTO>(url.toString(), dto, { params })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    chargeCancellationFee(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        status: BookingStatus | null,
        coversToCharge: number | null = null,
        orderRefundLineIds: string[] = []
    ): Observable<Booking> {
        const statusDto = status ? this.dtoAdapter.adaptBookingStatus(status) : undefined
        const dto = {
            status: statusDto,
            coversToCharge: coversToCharge,
            orderRefundLineIds,
        }
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            `/booking/${bookingId}` +
            '/cancellation-charge'
        const url = new URL(path, environment.apiBaseURL)
        const headers = {
            'Idempotency-Key': uuidv4(),
        }
        this._loading$.next(true)
        return this.http.put<BookingDTO>(url.toString(), dto, { headers })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    getBookingDetailsUrl(booking: Booking): string {
        return `${environment.widgetBaseURL}/booking/${booking.id}`
    }

    getBookingDetailsMenuUrl(booking: Booking): string {
        return `${environment.widgetBaseURL}/booking/${booking.id}/menu/edit`
    }

    getBookingDetailsIndividualMenuUrl(booking: Booking): string {
        return `${environment.widgetBaseURL}/booking/${booking.id}/menu/individual`
    }

    refundOrderLines(businessId: string, venueId: string, areaId: string, bookingId: string, lineItemIds: string[],
        status?: BookingStatus, refundDeposit = false): Observable<Booking> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/area/${areaId}/booking/${bookingId}/order-refund`
        const headers = { 'Idempotency-Key': uuidv4() }
        const body = { lineItemIds, ...(status ? { status: this.dtoAdapter.adaptBookingStatus(status), refundDeposit } : {}) }
        return this.http.put<BookingDTO>(new URL(path, environment.apiBaseURL).toString(), body, { headers })
            .pipe(map(dto => this.dtoAdapter.adaptBookingDto(dto)))
    }

    refundDeposit(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        status?: BookingStatus,
        amount?: number
    ): Observable<Booking> {
        const dto = status ? this.dtoAdapter.adaptBookingStatus(status) : undefined
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            `/booking/${bookingId}` +
            '/deposit-refund'
        const url = new URL(path, environment.apiBaseURL)
        if (amount !== undefined) {
            url.searchParams.set('amount', amount.toString())
        }
        const headers = {
            'Idempotency-Key': uuidv4(),
        }
        this._loading$.next(true)
        return this.http.put<BookingDTO>(url.toString(), dto, { headers })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    refundDepositPayment(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string,
        paymentId: string,
        amount: number
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            `/booking/${bookingId}` +
            `/deposit-payment/${paymentId}/refund`
        const url = new URL(path, environment.apiBaseURL)
        url.searchParams.set('amount', amount.toString())
        const headers = {
            'Idempotency-Key': uuidv4(),
        }
        this._loading$.next(true)
        return this.http.put<BookingDTO>(url.toString(), null, { headers })
            .pipe(
                finalize(() => this._loading$.next(false)),
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    refundCancellationCharge(
        businessId: string,
        venueId: string,
        areaId: string,
        bookingId: string
    ): Observable<Booking> {
        const path =
            'organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/area/${areaId}` +
            `/booking/${bookingId}` +
            '/cancellation-refund'
        const url = new URL(path, environment.apiBaseURL)
        const headers = {
            'Idempotency-Key': uuidv4(),
        }
        return this.http.post<BookingDTO>(url.toString(), null, { headers })
            .pipe(
                map(bookingDto => this.dtoAdapter.adaptBookingDto(bookingDto))
            )
    }

    updateOrderMessage(
        organisationId: string, businessId: string, venueId: string, booking: Booking, message: string | null
    ): Observable<Booking> {
        const path = `/organisation/${organisationId}/business/${businessId}/venue/${venueId}/order/${booking.order!.id}/message`
        return this.http.put<Order>(new URL(path, environment.apiBaseURL).toString(), { message }).pipe(
            map(() => {
                booking.order!.message = message
                return booking
            })
        )
    }

    putOrder(
        businessId: string,
        venueId: string,
        bookingId: string,
        order: Order
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/order'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.put<BookingDTO>(url.toString(), order)
            .pipe(
                map(dto => this.dtoAdapter.adaptBookingDto(dto))
            )
    }

    deleteOrder(
        businessId: string,
        venueId: string,
        booking: Booking
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${booking.id}` +
            '/order'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<void>(url.toString())
            .pipe(
                map(() => {
                    booking.order = null
                    return booking
                })
            )
    }

    createBookingDocket(
        businessId: string,
        venueId: string,
        bookingId: string,
        menuId: string,
        minimumCoursesRequired: number | null = null,
        maximumCoursesRequired: number | null = null
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/docket'
        const url = new URL(path, environment.apiBaseURL)
        url.searchParams.append('menuId', menuId)
        if (minimumCoursesRequired !== null) {
            url.searchParams.append('minimumCoursesRequired', String(minimumCoursesRequired))
        }
        if (maximumCoursesRequired !== null) {
            url.searchParams.append('maximumCoursesRequired', String(maximumCoursesRequired))
        }
        return this.http.post<BookingDTO>(url.toString(), null)
            .pipe(map(dto => this.dtoAdapter.adaptBookingDto(dto)))
    }

    removeBookingDocket(
        businessId: string,
        venueId: string,
        booking: Booking
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${booking.id}` +
            '/docket'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<void>(url.toString())
            .pipe(
                map(() => {
                    booking.docket = null
                    booking.order = null
                    return booking
                })
            )
    }

    updateBookingDocket(
        businessId: string,
        venueId: string,
        bookingId: string,
        docket: BookingDocket
    ): Observable<Booking> {
        const path = '/organisation' +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/booking/${bookingId}` +
            '/docket'
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptBookingDocket(docket)
        return this.http.put<BookingDTO>(url.toString(), dto)
            .pipe(map(bookingDto => this.dtoAdapter.adaptBookingDto(bookingDto)))
    }
}
